/**
 * Main App Component
 *
 * Combines all features:
 * - Deep linking (?method=ID)
 * - State persistence (localStorage)
 * - Language switching with full i18n support
 * - Accessibility (ARIA, keyboard nav)
 */

import React, { useState, useEffect } from 'react';
import { PlanProvider, usePlan } from './contexts/PlanContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { useDeepLinking } from './hooks/useDeepLinking';
import { useMethods, useFilters } from './hooks/useMethods';
import { MethodModal } from './components/MethodModal';
import { MethodList } from './components/MethodList';
import { FilterPanel } from './components/FilterPanel';
import { PlanSummary } from './components/PlanSummary';
import { InfoCard } from './components/InfoCard';
import type { LanguageCode } from './types/method-fields';

class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('Training Planner app error:', error, info);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center p-8">
            <p className="text-lg font-semibold text-zinc-800 mb-2">Something went wrong.</p>
            <p className="text-sm text-zinc-500">Please refresh the page to try again.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const AppContent: React.FC = () => {
  // Get settings from global window object
  const getSettings = () => {
    // Default settings for tests or when WordPress localization is not available
    const defaults = {
      apiUrl: '/wp-json/training-planner/v1',
      currentLang: 'en',
      siteTitle: 'Training Planner',
      languages: [
        { code: 'en', name: 'English', url: '/en/app/' },
        { code: 'pl', name: 'Polski', url: '/pl/app/' },
        { code: 'de', name: 'Deutsch', url: '/de/app/' },
        { code: 'da', name: 'Dansk', url: '/da/app/' },
        { code: 'uk', name: 'Українська', url: '/uk/app/' },
        { code: 'cs', name: 'Čeština', url: '/cs/app/' },
        { code: 'tr', name: 'Türkçe', url: '/tr/app/' },
        { code: 'ar', name: 'العربية', url: '/ar/app/' },
        { code: 'nl', name: 'Nederlands', url: '/nl/app/' },
        { code: 'es', name: 'Español', url: '/es/app/' },
      ],
      enableLocalStorage: true,
      appPageId: 123,
    };

    if (typeof window !== 'undefined' && window.tpSettings) {
      // Merge with defaults to ensure all required properties exist
      return {
        ...defaults,
        ...window.tpSettings,
      };
    }

    return defaults;
  };

  const settings = getSettings();

  // Language context - provides translations and language switching
  const { language, setLanguage, t } = useLanguage();

  // Toast notifications from plan context
  const { toast } = usePlan();

  // Deep linking
  const {
    selectedMethodId,
    setSelectedMethodId,
    isModalOpen,
    setIsModalOpen,
  } = useDeepLinking();

  // Mobile filter visibility toggle
  const [filtersVisible, setFiltersVisible] = useState(false);

  // Reset filter visibility when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setFiltersVisible(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Methods and filters - use language from context
  const { methods, filters, setFilters, loading: methodsLoading, error: methodsError } = useMethods({ lang: language });
  const { filtersData, loading: filtersLoading } = useFilters(language);

  // Handle language change
  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const newLang = e.target.value as LanguageCode;
    setLanguage(newLang);

    // Clear taxonomy-based filters (module, topic, tag) when language changes
    // because these term IDs are language-specific and may not exist in the new language.
    // Preserve duration filters as they are language-agnostic (numeric values).
    setFilters({
      lang: newLang,
      min_duration: filters.min_duration,
      max_duration: filters.max_duration,
      // Note: module, topic, tag are intentionally NOT preserved
    });
  };

  // Handle method card click
  const handleSelectMethod = (methodId: number): void => {
    setSelectedMethodId(methodId);
  };

  // Handle modal close
  const handleCloseModal = (): void => {
    setIsModalOpen(false);
  };

  return (
    <div className="flex flex-col min-h-screen bg-primary">
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <a
            href={(window as any).tpSettings?.homeUrl ?? '/'}
            className="flex items-center gap-3 no-underline"
            aria-label="Go to homepage"
          >
            <img
              src={`${(window as any).tpSettings?.pluginUrl ?? ''}assets/images/ely-logo.png`}
              alt="Energy Literacy for Youth"
              className="h-8 md:h-12 w-auto"
            />
            <span className="header-title">Training Planner</span>
          </a>

          {/* Language selector */}
          <div className="flex items-center gap-2">
            <label htmlFor="language-select" className="text-sm font-medium text-navy">
              {t('language')}:
            </label>
            <select
              id="language-select"
              aria-label={t('language')}
              value={language}
              onChange={handleLanguageChange}
              className="filter-select"
            >
              {settings.languages.map((lang: { code: string; name: string }) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex flex-col md:flex-row flex-1 max-w-7xl w-full mx-auto gap-6 p-4 md:p-6">
        {/* Mobile filter toggle */}
        <div className="md:hidden w-full">
          <button
            type="button"
            onClick={() => setFiltersVisible(prev => !prev)}
            aria-expanded={filtersVisible}
            aria-controls="filter-panel"
            className="btn-outline w-full flex items-center justify-center gap-2 mb-4"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {filtersVisible ? t('showLess') : t('showFilters')}
          </button>
        </div>

        {/* Filters sidebar */}
        <aside
          id="filter-panel"
          className={`w-full md:w-[304px] md:flex-shrink-0 ${filtersVisible ? 'block' : 'hidden'} md:block`}
        >
          <FilterPanel
            filters={filters}
            setFilters={setFilters}
            availableFilters={filtersData}
            loading={filtersLoading}
          />
        </aside>

        {/* Method list */}
        <section className="flex-1">
          {methodsError && (
            <div role="alert" className="alert alert-error">
              <strong>{t('error')}:</strong> {methodsError}
            </div>
          )}

          <InfoCard />

          <MethodList
            methods={methods}
            loading={methodsLoading}
            onSelectMethod={handleSelectMethod}
          />
        </section>
      </main>

      {/* Plan summary */}
      <aside className="border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto">
          <PlanSummary />
        </div>
      </aside>

      {/* EU Funding Footer */}
      <footer className="border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <img
            src={`${window.tpSettings?.pluginUrl ?? ''}assets/images/eu-footer-en.png`}
            alt="Funded by the European Union"
            className="w-full h-auto"
          />
        </div>
      </footer>

      {/* Method modal */}
      <MethodModal
        methodId={selectedMethodId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />

      {/* Toast notification */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm max-w-xs text-white ${
            toast.type === 'error' ? 'bg-red-600' : 'bg-zinc-800'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <PlanProvider>
        <AppContent />
      </PlanProvider>
    </LanguageProvider>
  );
};

export { AppErrorBoundary };
export default App;
